import { deprecated } from "typesafe-actions";

import type { IShoppingCartAddResponse, IShoppingCartData, IShoppingCartPosition, IShoppingCartSyncState } from "@/shop/types/shopping/cart";
import type { ServerToCartItem } from "@/shop/types/shopping/to-cart-item";

import type { CartPositionCollection } from "./constants";
import { CartActionTypes } from "./constants";

const { createStandardAction } = deprecated;

// FETCH

/**
 * Fetch the shopping cart
 */
export const fetch = createStandardAction(CartActionTypes.FETCH)();

/** Indicate cart as fetching */
export const fetchStart = createStandardAction(CartActionTypes.FETCH_START)();

/** Set new cart data in reducer  */
export const fetchSuccess = createStandardAction(CartActionTypes.FETCH_SUCCESS).map((cart: IShoppingCartData) => ({ payload: cart }));

/** Set error flags in reducer */
export const fetchFail = createStandardAction(CartActionTypes.FETCH_FAIL).map((error: Error) => ({ payload: error, error: true }));

// ADD

/**
 * Add an item to the shopping cart
 * - this action can be awaited when using `dispatch`
 * - ⚠️ Do not `put` this in saga, this will not wait for the saga to finish
 */
export const addItems = createStandardAction(CartActionTypes.ADD).map((items: ServerToCartItem[]) => ({
  payload: items,
  meta: { thunk: true }
}));

export const removeAllItemsFromCartThenAddItems = createStandardAction(CartActionTypes.REMOVE_ALL_THEN_ADD).map(
  (itemsAndCollections: { items: ServerToCartItem[]; collections: CartPositionCollection[]; callback?: () => void }) => ({
    payload: itemsAndCollections,
    meta: { thunk: true }
  })
);

/** Indicate adding item started */
export const addItemsStart = createStandardAction(CartActionTypes.ADD_START)();

/** Sets new cart state, show notification */
export const addItemsSuccess = createStandardAction(CartActionTypes.ADD_SUCCESS).map((cartData: IShoppingCartAddResponse, meta: { thunk: boolean }) => ({
  payload: cartData,
  meta
}));

/** Set error flags in reducer */
export const addItemsFail = createStandardAction(CartActionTypes.ADD_FAIL).map((err: Error, meta: { thunk: boolean }) => ({ payload: err, meta, error: true }));

// UPDATE

/** Update cart, e.g. for modifying quantity or enabling and disabling positions */
export const update = createStandardAction(CartActionTypes.UPDATE).map((cartData: IShoppingCartData) => ({ payload: cartData }));

export const updateStart = createStandardAction(CartActionTypes.UPDATE_START).map((cartData: IShoppingCartData) => ({ payload: cartData }));
export const updateFail = createStandardAction(CartActionTypes.UPDATE_FAIL).map((err: Error) => ({ payload: err, error: true }));
export const updateSuccess = createStandardAction(CartActionTypes.UPDATE_SUCCESS).map((cartData: IShoppingCartData) => ({ payload: cartData }));

// SYNC
export const checkSync = createStandardAction(CartActionTypes.CHECK_SYNC)();
export const checkSyncStart = createStandardAction(CartActionTypes.CHECK_SYNC_START)();
export const checkSyncSuccess = createStandardAction(CartActionTypes.CHECK_SYNC_SUCCESS).map((syncState: IShoppingCartSyncState) => ({ payload: syncState }));
export const checkSyncFail = createStandardAction(CartActionTypes.CHECK_SYNC_FAIL)();
export const checkSyncSkip = createStandardAction(CartActionTypes.CHECK_SYNC_SKIP)();

/**
 * Update position quantity, calls to this should be debounced
 */
export const changeQuantity = createStandardAction(CartActionTypes.CHANGE_QTY).map(
  ({ collection, id, qty }: { collection: CartPositionCollection; id: string; qty: number }) => ({
    payload: { collection, id, qty }
  })
);

/**
 * Update the number of items in the open shop cart. This can also be used to listen to changes in the open cart.
 */
export const changeOpenShopQuantity = createStandardAction(CartActionTypes.CHANGE_OPEN_QTY).map(({ qty }: { qty: number }) => ({
  payload: { qty }
}));

/**
 * Remove position.
 */
export const removePosition = createStandardAction(CartActionTypes.REMOVE_POS).map(
  ({ collection, id }: { collection: CartPositionCollection; id: string }) => ({
    payload: { collection, id }
  })
);

/**
 * Toggle position.
 */
export const togglePosition = createStandardAction(CartActionTypes.TOGGLE_POS).map(
  ({ collection, id, enabled }: { collection: CartPositionCollection; id: string; enabled?: boolean }) => ({
    payload: { collection, id, enabled }
  })
);

/**
 * Toggle position.
 */
export const toggleAllPositions = createStandardAction(CartActionTypes.TOGGLE_ALL_POS).map(
  ({ collection, enabled }: { collection: CartPositionCollection; enabled?: boolean }) => ({
    payload: { collection, enabled }
  })
);

/**
 * Update position customer material number
 */
export const updateCustomerMaterialNumber = createStandardAction(CartActionTypes.UPDATE_CUSTOMER_MATNR).map(
  ({ collection, id, newCustomerMaterialNumber }: { collection: CartPositionCollection; id: string; newCustomerMaterialNumber: string }) => ({
    payload: { collection, id, newCustomerMaterialNumber }
  })
);

/**
 * Update position `sequenceNumber`
 */
export const updateSequenceNumber = createStandardAction(CartActionTypes.UPDATE_SEQUENCE_NUMBER).map(
  ({ collection, id, sequenceNumber }: { collection: CartPositionCollection; id: string; sequenceNumber: number }) => ({
    payload: { collection, id, sequenceNumber }
  })
);

/**
 * Update the sequence of the cart
 */
export const updateAllSequenceNumbers = createStandardAction(CartActionTypes.UPDATE_ALL_SEQUENCE_NUMBERS).map(
  ({ collection, updatedIdsSequence }: { collection: CartPositionCollection; updatedIdsSequence: string[] }) => ({
    payload: { collection, updatedIdsSequence }
  })
);

/**
 * Update position comment
 */
export const updatePositionComment = createStandardAction(CartActionTypes.UPDATE_POS_COMMENT).map(
  ({ collection, id, comment }: { collection: CartPositionCollection; id: string; comment: string }) => ({
    payload: { collection, id, comment }
  })
);

/**
 * Update position comment
 */
export const updatePositionEquipmentNumber = createStandardAction(CartActionTypes.UPDATE_EQUIPMENT_NUMBER).map(
  ({ collection, id, equipmentNumber }: { collection: CartPositionCollection; id: string; equipmentNumber: string }) => ({
    payload: { collection, id, equipmentNumber }
  })
);

/**
 * Update position roll number
 */
export const updatePositionRollNumber = createStandardAction(CartActionTypes.UPDATE_ROLL_NUMBER).map(
  ({ collection, id, rollNumber }: { collection: CartPositionCollection; id: string; rollNumber: string }) => ({
    payload: { collection, id, rollNumber }
  })
);

/**
 * Remove all positions in a cart collection
 */
export const removeAllPositions = createStandardAction(CartActionTypes.REMOVE_ALL_POSITIONS).map(
  (payload: {
    collection: CartPositionCollection | CartPositionCollection[];
    /**
     * removeAllPositions is using put by default so the saga can not be awaited
     * if awaitable is set to true the saga will be awaited
     * and following actions will be dispatched after the saga is finished
     */
    awaitable?: boolean;
  }) => ({ payload })
);

/**
 * split cart position
 */
export const splitCartPosition = createStandardAction(CartActionTypes.SPLIT_CART_POSITION).map(
  ({ collection, targetPosition }: { collection: CartPositionCollection; targetPosition: IShoppingCartPosition }) => ({
    payload: { collection, targetPosition }
  })
);

export const moveToOnRequestPositions = createStandardAction(CartActionTypes.MOVE_TO_ON_REQUEST_POSITIONS).map((payload: string[]) => ({ payload }));
